import { Injectable } from '@angular/core'
import { EntitySignalStore } from '@awork/core/state/signal-store/entitySignalStore'
import type { FilterEntityType } from '@awork/_shared/models/filters.types'
import { DraftFilterEntity } from '@awork/_shared/models/filters.model'

@Injectable({ providedIn: 'root' })
export class DraftFilterStore extends EntitySignalStore<DraftFilterEntity> {
  constructor() {
    super({
      name: 'draftFilter',
      entityConstructor: draft => new DraftFilterEntity(draft)
    })
  }

  /**
   * Adds a persisted draft to the store when it has a valid ID.
   * @param {DraftFilterEntity} draft - Draft to add
   */
  addDraft(draft: DraftFilterEntity): void {
    if (!draft?.id) {
      return
    }

    this.add(draft)
  }

  /**
   * Replaces drafts for the provided entity type by setting them in the store.
   * @param {FilterEntityType} entityType - Draft entity type
   * @param {DraftFilterEntity[]} drafts - Drafts fetched from API
   */
  setDraftsForEntityType(entityType: FilterEntityType, drafts: DraftFilterEntity[]): void {
    const existingDrafts = this.entities().filter(draft => draft.entityType !== entityType)

    if (!drafts.length) {
      this.set(existingDrafts)

      return
    }

    const newDrafts = drafts.filter(draft => !!draft.id).map(draft => new DraftFilterEntity(draft))

    if (!newDrafts.length) {
      return
    }

    this.set([...existingDrafts, ...newDrafts])
  }

  /**
   * Upserts a draft by ID, ensuring the entity includes the ID reference.
   * @param {string} draftId - Draft ID
   * @param {DraftFilterEntity} draft - Draft payload
   */
  upsertDraft(draftId: string, draft: DraftFilterEntity): void {
    if (!draftId) {
      return
    }

    const persistedDraft = new DraftFilterEntity({
      ...draft,
      id: draftId
    })

    this.upsert(draftId, persistedDraft)
  }

  /**
   * Removes a draft from the store by ID.
   * @param {string} draftId - Draft ID
   */
  removeDraft(draftId: string): void {
    if (!draftId) {
      return
    }

    this.remove(draftId)
  }
}
